import React, { useMemo, useState } from 'react'
import clsx from 'clsx'
import type { UsageBreakdownRow } from '@shared/common-types.js'
import { toVersionShares } from './version-share.js'
import type { VersionGroupBy } from './version-share.js'

const numberFormat = new Intl.NumberFormat('zh-CN')

/**
 * 分组的颜色，按新旧排：最新的一组永远是蓝色，往前依次橙、青、黄……
 * 相邻两段的颜色在色弱下也分得开（dataviz 参考色板的前几个槽位，顺序不能乱）。超出的旧版本统一灰色
 */
const GROUP_COLORS = [
  'bg-[#2a78d6] dark:bg-[#3987e5]',
  'bg-[#eb6834] dark:bg-[#d95926]',
  'bg-[#1baf7a] dark:bg-[#199e70]',
  'bg-[#eda100] dark:bg-[#c98500]',
  'bg-[#e87ba4] dark:bg-[#d55181]',
  'bg-[#6250d6] dark:bg-[#9085e9]',
]
const OLDER_COLOR = 'bg-gray-400 dark:bg-gray-500'

/** 段内写版本号的最小占比，再窄就放不下了 */
const MIN_LABEL_SHARE = 0.06

function formatPercent(value: number): string {
  if (value > 0 && value < 0.01) return '<1%'
  if (value < 1 && value > 0.99) return '>99%'
  return `${Math.round(value * 100)}%`
}

type Props = {
  title: string
  icon?: React.ReactNode
  /** 服务端按 major.minor 归好的行，一人一行 */
  rows: UsageBreakdownRow[]
  /** 颜色和图例按大版本分（iOS），还是每个版本一组（App，大版本都是 1） */
  groupBy: VersionGroupBy
  /** 版本号前面加什么，「iOS 」让说明读成「低于 iOS 26.6」 */
  prefix?: string
  /** 脚注里的「iOS 版本」「App 版本」 */
  subject: string
}

/**
 * 版本占比：版本归到 major.minor，从低到高排成一条 100% 的横条。
 * 悬停或点一段看「低于这个版本」的人占多少，决定最低支持版本时用
 */
export default function VersionShareCard({ title, icon, rows, groupBy, prefix = '', subject }: Props) {
  const { versions, groups, total, unknown } = useMemo(() => toVersionShares(rows, groupBy), [rows, groupBy])
  const [hovered, setHovered] = useState<string | null>(null)
  const [pinned, setPinned] = useState<string | null>(null)
  const activeVersion = hovered ?? pinned
  const active = versions.find((item) => item.version === activeVersion)

  const colorOf = (group: string) => {
    const rank = groups.length - 1 - groups.findIndex((item) => item.group === group)
    return GROUP_COLORS[rank] ?? OLDER_COLOR
  }
  const toggle = (version: string) => setPinned((value) => (value === version ? null : version))

  return (
    <div className="flex flex-col gap-3 rounded-xl bg-white p-3 dark:bg-slate-700 md:p-4">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2 text-base font-semibold dark:text-white">
          {icon}
          {title}
        </div>
        <span className="text-sm text-gray-500 dark:text-gray-400">人数</span>
      </div>

      {versions.length === 0 ? (
        <div className="flex h-24 items-center justify-center text-sm text-gray-400">没有数据</div>
      ) : (
        <>
          <div className="flex h-9 w-full gap-[2px]" onMouseLeave={() => setHovered(null)}>
            {versions.map((item) => (
              <button
                key={item.version}
                type="button"
                title={`${prefix}${item.version}：${numberFormat.format(item.people)} 人，${formatPercent(item.share)}`}
                onMouseEnter={() => setHovered(item.version)}
                onClick={() => toggle(item.version)}
                style={{ flexGrow: item.people, flexBasis: 0 }}
                className={clsx(
                  'flex min-w-[3px] items-center justify-center overflow-hidden rounded text-xs font-medium text-gray-950 transition-opacity',
                  colorOf(item.group),
                  active && active.version !== item.version && 'opacity-40',
                )}
              >
                {item.share >= MIN_LABEL_SHARE && item.version}
              </button>
            ))}
          </div>

          <div className="min-h-[1.25rem] text-sm tabular-nums dark:text-gray-200">
            {active ? (
              <>
                低于 {prefix}
                {active.version}：<b>{formatPercent(active.below)}</b>
                <span className="text-gray-400">（{numberFormat.format(Math.round(active.below * total))} 人）</span>
                <span className="mx-2 text-gray-300 dark:text-gray-500">·</span>
                {active.version} 及以上：<b>{formatPercent(1 - active.below)}</b>
              </>
            ) : (
              <span className="text-gray-400">悬停或点一段，看低于这个版本的人占多少</span>
            )}
          </div>

          {/* 每个版本自成一组时图例和下面的表重复，不画 */}
          {groupBy === 'major' && (
            <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-gray-600 dark:text-gray-300">
              {groups.map((item) => (
                <span key={item.group} className="flex items-center gap-1.5 tabular-nums">
                  <span className={clsx('h-2.5 w-2.5 rounded-sm', colorOf(item.group))} />
                  {prefix}
                  {item.group}
                  <span className="text-gray-400">{formatPercent(item.share)}</span>
                </span>
              ))}
            </div>
          )}

          <table className="w-full text-sm tabular-nums">
            <thead>
              <tr className="text-xs text-gray-500 dark:text-gray-400">
                <th className="py-1 text-left font-normal">版本</th>
                <th className="py-1 text-right font-normal">人数</th>
                <th className="py-1 text-right font-normal">占比</th>
                <th className="py-1 text-right font-normal" title="比这个版本低的所有版本加起来">
                  低于它
                </th>
              </tr>
            </thead>
            <tbody onMouseLeave={() => setHovered(null)}>
              {versions.map((item) => (
                <tr
                  key={item.version}
                  onMouseEnter={() => setHovered(item.version)}
                  onClick={() => toggle(item.version)}
                  className={clsx(
                    'cursor-pointer border-t border-gray-100 dark:border-slate-600 dark:text-gray-200',
                    active?.version === item.version && 'bg-gray-50 dark:bg-slate-600',
                  )}
                >
                  <td className="py-1">
                    <span className="flex items-center gap-1.5">
                      <span className={clsx('h-2.5 w-2.5 rounded-sm', colorOf(item.group))} />
                      {item.version}
                    </span>
                  </td>
                  <td className="py-1 text-right text-gray-500 dark:text-gray-400">
                    {numberFormat.format(item.people)}
                  </td>
                  <td className="py-1 text-right">{formatPercent(item.share)}</td>
                  <td className="py-1 text-right">{formatPercent(item.below)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </>
      )}

      <p className="text-[11px] text-gray-400">
        按人数算，每人只算一次，取这段时间里他最后一次打开 App 时的 {subject}，所以各版本加起来是 100%。
        {unknown > 0 && `另有 ${numberFormat.format(unknown)} 人没带版本号，不计入。`}受国家 / 版本 / 事件过滤。
      </p>
    </div>
  )
}
